import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, Loader2, Lock, ShieldAlert } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { ParticleField } from "@/components/ParticleField";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password !== confirm) {
      setError("Passwords don't match");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      setDone(true);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <main data-testid="reset-password-page" className="relative min-h-screen bg-[#05030A] flex items-center justify-center px-6 overflow-hidden">
      <div className="absolute inset-0" style={{ background: "radial-gradient(ellipse 55% 45% at 50% 40%, rgba(139,92,246,0.18), transparent 70%)" }} />
      <ParticleField className="absolute inset-0" />
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
        className="relative z-10 w-full max-w-sm rounded-2xl border border-white/10 bg-[#0D0B12]/90 backdrop-blur-xl p-8"
      >
        {done ? (
          <div className="text-center" data-testid="reset-success">
            <CheckCircle2 className="w-14 h-14 text-[#8B5CF6] mx-auto" strokeWidth={1.5} />
            <h1 className="mt-5 font-display text-3xl tracking-tight text-white">Password updated</h1>
            <p className="mt-3 text-sm text-slate-400">You're all set — log in with your new password.</p>
            <Link
              to="/"
              data-testid="reset-home-link"
              className="mt-8 inline-block px-8 py-3 rounded-xl bg-[#8B5CF6] hover:bg-[#7C3AED] text-white text-sm font-bold tracking-[0.15em] transition-[background-color] duration-300"
            >
              BACK TO STORE
            </Link>
          </div>
        ) : !token ? (
          <div className="text-center" data-testid="reset-invalid">
            <ShieldAlert className="w-14 h-14 text-amber-400 mx-auto" strokeWidth={1.5} />
            <h1 className="mt-5 font-display text-3xl tracking-tight text-white">Invalid reset link</h1>
            <p className="mt-3 text-sm text-slate-400">This link is missing its token. Request a fresh one from the login dialog.</p>
          </div>
        ) : (
          <>
            <h1 className="font-display text-3xl tracking-tight text-white text-center">New password</h1>
            <p className="mt-2 text-sm text-slate-400 text-center">Pick something strong. Min 6 characters.</p>
            <form onSubmit={submit} className="mt-8 space-y-4">
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                <Input
                  type="password"
                  required
                  minLength={6}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="New password"
                  data-testid="reset-password-input"
                  className="pl-10 bg-white/5 border-white/10 text-white placeholder:text-slate-600 focus-visible:ring-[#8B5CF6]"
                />
              </div>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                <Input
                  type="password"
                  required
                  minLength={6}
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  placeholder="Confirm new password"
                  data-testid="reset-confirm-input"
                  className="pl-10 bg-white/5 border-white/10 text-white placeholder:text-slate-600 focus-visible:ring-[#8B5CF6]"
                />
              </div>
              {error && <p className="text-sm text-red-400" data-testid="reset-error">{error}</p>}
              <button
                type="submit"
                disabled={loading}
                data-testid="reset-submit-button"
                className="w-full py-3 rounded-xl bg-[#8B5CF6] hover:bg-[#7C3AED] disabled:opacity-60 text-white text-sm font-bold tracking-[0.15em] transition-[background-color,opacity] duration-300 flex items-center justify-center gap-2"
              >
                {loading && <Loader2 className="w-4 h-4 animate-spin" />}
                UPDATE PASSWORD
              </button>
            </form>
          </>
        )}
      </motion.div>
    </main>
  );
}
